'use client';

import type { MutableRefObject } from 'react';
import { useCallback, useRef, useState } from 'react';
import type { Key } from 'react-aria';
import { flushSync } from 'react-dom';
import invariant from 'tiny-invariant';
import type { GetSize, LayoutInfo } from '../layouts/types';
import type { ListFilterState } from '../List/types';
import type { RectSize } from './useSize';

type UseUpdateKeySizeProps = {
  estimatedItemHeight: number;
  estimatedHeaderHeight?: number;
};

/**
 * This hook is useful for tracking the sizes of items in a list. It supports
 * returning the measured, estimated, or fixed height of an item or header. It also
 * supports offseting the scroll position when an item is resized.
 */

export function useUpdateKeySize<T extends object>(
  ref: React.RefObject<HTMLElement | null>,
  options: UseUpdateKeySizeProps,
  {
    state,
    scrollCache,
  }: {
    state?: ListFilterState<T>;
    scrollCache: MutableRefObject<{ x: number; y: number }>;
  }
) {
  const { estimatedItemHeight, estimatedHeaderHeight = 40 } = options;
  const [keySizeMap, setKeySizeMap] = useState(new Map<Key, RectSize>());
  const isStabilizationScrollRef = useRef<boolean>(false);

  /**
   * Get either the measured, estimated, or fixed
   * height of an item or header.
   */

  const getSize = useCallback<GetSize<T>>(
    (key, type, node) => {
      /**
       * If an item or header has a fixed height specified,
       * use that value.
       */

      if (type === 'header' && node?.props?.headerHeight) {
        return {
          height: node.props.headerHeight,
          isMeasured: true,
          width: Infinity,
        };
      }

      if (type === 'item' && node?.props?.height) {
        return {
          height: node.props.height,
          isMeasured: true,
          width: Infinity,
        };
      }

      /**
       * If the item has been measured, use the measured
       * size from the keySizeMap.
       */

      const measuredSize = keySizeMap.get(key);

      if (measuredSize !== undefined) {
        return {
          height: measuredSize.height,
          isMeasured: true,
          width: measuredSize.width,
        };
      }

      /**
       * Otherwise, use the estimated height.
       */

      return {
        height: type === 'header' ? estimatedHeaderHeight : estimatedItemHeight,
        isMeasured: false,
        width: Infinity,
      };
    },
    [keySizeMap, estimatedHeaderHeight, estimatedItemHeight]
  );

  /**
   * Update the size of an item in the collection.
   * The component that calls this hook should run it on
   * useLayoutEffect (upon mount) and when the resize
   * observer fires.
   */

  const setSize = useCallback(
    (
      key: Key,
      size: { height: number; width: number },
      {
        layoutInfo,
      }: {
        layoutInfo: Map<Key, LayoutInfo>;
      }
    ) => {
      const { height, width } = size;

      if (!height) {
        return;
      }

      const info = layoutInfo.get(key);

      if (!info) {
        return;
      }

      invariant(
        info?.type !== 'section',
        `
                Cannot update the size of a section. Section heights
                are determined by their children.
              `
      );

      const {
        height: prevHeight,
        width: prevWidth,
        isMeasured,
      } = getSize(
        key,
        info.type,
        state?.collection.getItem(
          info.type === 'header' ? info.parentKey : info.key
        ) || undefined
      );

      const deltaY = height - prevHeight;

      const setSize = () => {
        setKeySizeMap((prev) => {
          const next = new Map(prev);
          next.set(key, { height, width });
          return next;
        });
      };

      // sometimes a resize observer can return subpixel values
      // that are different from getBoundingClientRect. Ignore
      // these values.
      if (deltaY !== 0 && Math.abs(deltaY) > 0.5) {
        const scrollTop = scrollCache.current.y;

        // stabilize our scroll position if the resized element is above
        // our current visible rect.
        if (info.rect.y + info.rect.height < scrollTop) {
          const nextY = scrollTop + deltaY;
          // eslint-disable-next-line no-param-reassign
          scrollCache.current.y = nextY;

          const resetScrollFlag = () => {
            setTimeout(() => {
              isStabilizationScrollRef.current = false;
            }, 0);
          };
          isStabilizationScrollRef.current = true;

          // if our width has changed between re-renders, then we are horizontally
          // resizing our viewport, and need to use flushSync to update our item
          // sizes and stabilize our viewport. `queueMicrotask` is required to use
          // flushSync in a lifecycle method. This prevents flickering which is
          // otherwise not noticeable when simply scrolling.
          if (prevWidth !== width && prevWidth !== Infinity) {
            queueMicrotask(() => {
              ref.current!.scrollTop = nextY;
              flushSync(setSize);
              resetScrollFlag();
            });

            return;
          }

          ref.current!.scrollTop = nextY;
          resetScrollFlag();
        }

        setSize();
      } else if (!isMeasured) {
        setSize();
      }
    },
    [getSize, ref, scrollCache, state?.collection]
  );

  return {
    getSize,
    setSize,
    isStabilizationScrollRef,
  };
}
